/**
 * Anti-corruption layer for site plan data arriving from the server.
 *
 * The API returns PascalCase keys directly from C# models. These functions
 * translate the raw payloads into stable camelCase POJOs so that the rest of
 * the application is never coupled to the wire format.
 *
 * @module searchResultsMap/services/processSitePlans
 */

/**
 * @typedef {Object} SvgLayer
 * @property {string} url
 * @property {string} svgLayerType
 * @property {number} order
 */

/**
 * @typedef {Object} SitePlan
 * @property {number}    communityId
 * @property {number}    topRightLatitude
 * @property {number}    topRightLongitude
 * @property {number}    bottomLeftLatitude
 * @property {number}    bottomLeftLongitude
 * @property {number}    rotation
 * @property {number}    viewBoxWidth
 * @property {number}    viewBoxHeight
 * @property {string}    svgMask
 * @property {string}    mapImageUrl
 * @property {boolean}   isInteractiveSitePlan
 * @property {number}    sitePlanId
 * @property {number}    distanceFromCommunityCenter
 * @property {SvgLayer[]} svgLayers
 * @property {number}    topBottomDistance
 * @property {number}    topBottomLatitude
 * @property {number}    topBottomLongitude
 * @property {{ lat: number, lng: number }|null} centerPosition
 * @property {number}    area
 */

/**
 * @typedef {Object} SitePlanLot
 * @property {number}        latitude
 * @property {number}        longitude
 * @property {string}        polygon
 * @property {string}        label
 * @property {number[]}      housePlanIds
 * @property {string|null}   housingUnitId
 * @property {string}        id
 * @property {string}        status
 */

/**
 * @typedef {Object} InteractiveSitePlan
 * @property {number}        communityId
 * @property {number}        sitePlanId
 * @property {SitePlanLot[]} sitePlanLots
 */

/**
 * Layer types the application supports, keyed by SvgLayerType string.
 * Layers not in this set are dropped at the anti-corruption boundary
 * so unknown types never propagate into the rendering pipeline.
 */
const SUPPORTED_LAYER_TYPES = new Set([
  'StreetNames',
  'Walkway',
  'Water',
  'Golf',
  'Grass',
  'Ground',
  'Street',
  'Curbs',
  'Main',
  'MainLots',
  'LotPolygons',
]);

function isSupportedLayer(layer) {
  return SUPPORTED_LAYER_TYPES.has(layer.SvgLayerType);
}

function hasValidBoundsCoordinates(raw) {
  return Number.isFinite(raw.TopRightLatitude) &&
    Number.isFinite(raw.TopRightLongitude) &&
    Number.isFinite(raw.BottomLeftLatitude) &&
    Number.isFinite(raw.BottomLeftLongitude);
}

function computeSitePlanCenter(raw) {
  if (!hasValidBoundsCoordinates(raw)) {
    return null;
  }

  return {
    lat: (raw.TopRightLatitude + raw.BottomLeftLatitude) / 2,
    lng: (raw.TopRightLongitude + raw.BottomLeftLongitude) / 2,
  };
}

function computeSitePlanArea(raw) {
  if (!hasValidBoundsCoordinates(raw)) {
    return 0;
  }

  const latHeight = Math.abs(raw.TopRightLatitude - raw.BottomLeftLatitude);
  const lngWidth = Math.abs(raw.TopRightLongitude - raw.BottomLeftLongitude);
  return latHeight * lngWidth;
}

/**
 * Normalises a raw SvgLayer object from the API into a camelCase POJO.
 *
 * @param {Object} raw
 * @returns {SvgLayer}
 */
export function processSvgLayer(raw) {
  return {
    url: raw.Url,
    svgLayerType: raw.SvgLayerType,
    order: raw.Order,
  };
}

/**
 * Normalises a raw SitePlanLot object from the API into a camelCase POJO.
 *
 * @param {Object} raw
 * @returns {SitePlanLot}
 */
export function processSitePlanLot(raw) {
  return {
    latitude: raw.Latitude,
    longitude: raw.Longitude,
    polygon: raw.Polygon,
    label: raw.Label,
    housePlanIds: raw.HousePlanIds ?? [],
    housingUnitId: raw.HousingUnitId ?? null,
    id: raw.Id,
    status: raw.Status,
  };
}

/**
 * Normalises a raw SitePlan object from the API into a camelCase POJO.
 *
 * @param {Object} raw
 * @returns {SitePlan}
 */
export function processSitePlan(raw) {
  const centerPosition = computeSitePlanCenter(raw);
  const area = computeSitePlanArea(raw);

  return {
    communityId: raw.CommunityId,
    topRightLatitude: raw.TopRightLatitude,
    topRightLongitude: raw.TopRightLongitude,
    bottomLeftLatitude: raw.BottomLeftLatitude,
    bottomLeftLongitude: raw.BottomLeftLongitude,
    rotation: raw.Rotation,
    viewBoxWidth: raw.ViewBoxWidth,
    viewBoxHeight: raw.ViewBoxHeight,
    svgMask: raw.SvgMask,
    mapImageUrl: raw.MapImageUrl,
    isInteractiveSitePlan: raw.IsInteractiveSitePlan,
    sitePlanId: raw.SitePlanId,
    distanceFromCommunityCenter: raw.DistanceFromCommunityCenter,
    svgLayers: (raw.SvgLayers ?? []).filter(isSupportedLayer).map(processSvgLayer),
    topBottomDistance: raw.TopBottomDistance,
    topBottomLatitude: raw.TopBottomLatitude,
    topBottomLongitude: raw.TopBottomLongitude,
    centerPosition,
    area,
  };
}

/**
 * Normalises a raw InteractiveSitePlan object from the API into a camelCase POJO.
 *
 * @param {Object} raw
 * @returns {InteractiveSitePlan}
 */
export function processInteractiveSitePlan(raw) {
  return {
    communityId: raw.CommunityId,
    sitePlanId: raw.SitePlanId,
    sitePlanLots: (raw.SitePlanLots ?? []).map(processSitePlanLot),
  };
}

/**
 * Normalises an array or keyed object of site plan records into a
 * Record<string(SitePlanId), T> for O(1) lookups by site plan ID.
 *
 * Accepts both the raw array shape returned by the API and the already-keyed
 * object shape that may exist in the Redux store, so callers are insulated
 * from storage format changes.
 *
 * @template T
 * @param {T[] | Record<string, T> | null | undefined} rawData
 * @returns {Record<string, T>}
 */
export function normalizeBySitePlanId(rawData) {
  if (!rawData) {
    return {};
  }
  const source = Array.isArray(rawData) ? rawData : Object.values(rawData);
  return source.reduce((acc, item) => {
    acc[String(item.sitePlanId)] = item;
    return acc;
  }, {});
}
